/**
 * Model-stream watch store (Zustand).
 *
 * One SSE connection carries every model any surface on this page is watching
 * (ADR 0023: "the user keeps one SSE connection"). This store is where the
 * two sides meet: `useModelStream` registers what a surface wants, and
 * `useSessionEvents` — which owns the connection — reads the wanted set to
 * build its query string, posts the difference when it changes, and hands
 * arriving frames back here.
 *
 * The wanted set is DERIVED from who is listening rather than stored beside
 * it. Two surfaces on one model is a normal page (the board in the agent app
 * and a summary in the frame), and a separate count is a second answer to
 * "is anyone still watching this" that can disagree with the first.
 */

import { create } from "zustand";

import {
  modelToken,
  type ModelKey,
  type StreamEvent,
} from "@/api/streamEvents";
import { createLogger } from "@/utils/logger";

const logger = createLogger("ModelStreams");

/** What a watching surface is told. */
export interface ModelStreamListener {
  /** One event on the model, already de-duplicated by `seq`. */
  onEvent: (event: StreamEvent) => void;
  /**
   * The server tore this watch down because it could not keep this connection
   * up. The fold this surface holds may be missing changes, so the surface
   * re-reads it; the re-watch is this store's job and happens without the
   * surface asking.
   */
  onReset: () => void;
}

interface ModelStreamsState {
  /** Listeners by model token. A token with no listeners is not wanted. */
  listeners: Record<string, ModelStreamListener[]>;
  /**
   * Tokens the server refused INDIVIDUALLY, each in its own
   * `POST /events/subscriptions`. One token per request is what makes that
   * answer name one token, so a refusal here is about the model it names and
   * nothing else. They leave `wanted`.
   *
   * A refusal lasts as long as the surface asking for it. Clearing it on
   * reconnect would ask again immediately and get the same answer forever;
   * clearing it when the last listener goes means a board that remounts asks
   * once more, which is the recovery when the access behind it is restored.
   */
  refused: string[];
  /**
   * Tokens held out of the NEXT connect's query string, and only that.
   *
   * `GET /events` refuses the WHOLE request when any one `watch` is denied, so
   * the client cannot learn WHICH token was refused from that answer. It knows
   * only that the set it sent contains at least one it may not have. Dropping
   * the whole set from `wanted` on that evidence is what turned a hot
   * reconnect loop into a permanent silent stall: a ward roster that was never
   * denied went out with the stay that was, `wanted` went empty, and
   * `syncWatches` had nothing left to differ on, so every board on the page
   * re-read its fold once and then never updated again for the life of the
   * mount.
   *
   * So the connect-403 answer is coarse and TEMPORARY. The tokens stay wanted;
   * they are merely kept out of the query string of the reconnect that follows,
   * which then succeeds. `applyWatchSet` clears this the moment a `connected`
   * frame arrives, because the deferral has done its whole job by then, and
   * `syncWatches` re-offers each token through the per-token endpoint. Only
   * what THAT refuses becomes `refused`. Coarse first, then precise, with the
   * precise step actually running.
   */
  deferredFromConnect: string[];
  /** The models this page wants to watch: listened to, minus refused. */
  wanted: string[];
  /** The models the SERVER says this connection watches. */
  confirmed: string[];
  /** The connection the server named in its `connected` frame. */
  connectionId: string | null;
  /** Highest `seq` applied per model token. */
  appliedSeq: Record<string, number>;

  listen: (model: ModelKey, listener: ModelStreamListener) => () => void;
  refuseWatches: (tokens: string[]) => void;
  deferWatchesFromConnect: (tokens: string[]) => void;
  connectQueryTokens: () => string[];
  applyWatchSet: (connectionId: string, watching: string[]) => void;
  connectionLost: () => void;
  deliver: (event: StreamEvent) => void;
  resetModel: (model: ModelKey) => void;
}

function wantedFrom(
  listeners: Record<string, ModelStreamListener[]>,
  refused: string[],
): string[] {
  return Object.keys(listeners)
    .filter(
      (token) => listeners[token].length > 0 && !refused.includes(token),
    )
    .sort();
}

export const useModelStreamsStore = create<ModelStreamsState>((set, get) => ({
  listeners: {},
  refused: [],
  deferredFromConnect: [],
  wanted: [],
  confirmed: [],
  connectionId: null,
  appliedSeq: {},

  listen: (model, listener) => {
    const token = modelToken(model);
    set((state) => {
      const listeners = {
        ...state.listeners,
        [token]: [...(state.listeners[token] ?? []), listener],
      };
      return { listeners, wanted: wantedFrom(listeners, state.refused) };
    });

    return () => {
      set((state) => {
        const remaining = (state.listeners[token] ?? []).filter(
          (candidate) => candidate !== listener,
        );
        const listeners = { ...state.listeners };
        if (remaining.length === 0) {
          delete listeners[token];
        } else {
          listeners[token] = remaining;
        }
        // The applied cursor and any refusal go with the last listener. A
        // surface that remounts reads the fold again and starts from that
        // fold's version, so a cursor kept across the gap would drop the
        // events between them; and asking again is how a watch recovers once
        // the access behind it is restored.
        const appliedSeq = { ...state.appliedSeq };
        const gone = remaining.length === 0;
        const refused = gone
          ? state.refused.filter((candidate) => candidate !== token)
          : state.refused;
        const deferredFromConnect = gone
          ? state.deferredFromConnect.filter((candidate) => candidate !== token)
          : state.deferredFromConnect;
        if (gone) delete appliedSeq[token];
        return {
          listeners,
          refused,
          deferredFromConnect,
          wanted: wantedFrom(listeners, refused),
          appliedSeq,
        };
      });
    };
  },

  refuseWatches: (tokens) => {
    if (tokens.length === 0) return;
    logger.warn("Watches refused by the server", { tokens });
    set((state) => {
      const refused = [...new Set([...state.refused, ...tokens])];
      const appliedSeq = { ...state.appliedSeq };
      for (const token of tokens) delete appliedSeq[token];
      return {
        refused,
        appliedSeq,
        wanted: wantedFrom(state.listeners, refused),
        confirmed: state.confirmed.filter((c) => !tokens.includes(c)),
      };
    });
    // The surfaces are told the same thing a reset tells them: re-read the
    // fold. The read is where the truth is — it answers 403 when the access is
    // really gone, which is what a nurse needs to see, rather than a board
    // that just stops moving.
    for (const token of tokens) {
      for (const listener of get().listeners[token] ?? []) {
        listener.onReset();
      }
    }
  },

  deferWatchesFromConnect: (tokens) => {
    if (tokens.length === 0) return;
    logger.warn("Connect refused the whole watch set; deferring it to the per-token endpoint", {
      tokens,
    });
    set((state) => {
      // The applied cursor goes, exactly as it does on a reset and on a
      // refusal. All three tell the surface the same thing — re-read the fold
      // — and after that read the surface is at the fold's version, which the
      // store does not know. Keeping a stale cursor here would let events the
      // re-read already folded in through as if they were new, and applying
      // one twice is what `seq` exists to prevent.
      const appliedSeq = { ...state.appliedSeq };
      for (const token of tokens) delete appliedSeq[token];
      return {
        deferredFromConnect: [...new Set([...state.deferredFromConnect, ...tokens])],
        appliedSeq,
        // The confirmed set belonged to a connection that no longer exists, and
        // these tokens are the ones whose state is now least certain.
        confirmed: state.confirmed.filter((token) => !tokens.includes(token)),
      };
    });
    // The surfaces are told the same thing a reset tells them: re-read the
    // fold. The read is where the truth is — it answers 403 when the access is
    // really gone, which is what a nurse needs to see, rather than a board that
    // just stops moving. It is told to every surface in the set, not only the
    // one that was denied, because the connect answer does not say which.
    for (const token of tokens) {
      for (const listener of get().listeners[token] ?? []) {
        listener.onReset();
      }
    }
  },

  connectQueryTokens: () => {
    const { wanted, deferredFromConnect } = get();
    return wanted.filter((token) => !deferredFromConnect.includes(token));
  },

  applyWatchSet: (connectionId, watching) => {
    // The deferral is spent. It existed to get ONE connect past a set the
    // server refuses as a whole, and that connect has just succeeded, so every
    // deferred token goes back to being an ordinary difference between
    // `wanted` and `confirmed` for syncWatches to offer one at a time. Holding
    // it any longer is what would leave a board that was never denied watching
    // nothing for the rest of the mount.
    set({ connectionId, confirmed: [...watching].sort(), deferredFromConnect: [] });
  },

  connectionLost: () => {
    // The watch set belongs to the connection, so it dies with it. Keeping it
    // would make the next connect think the server already knows, and the
    // reconnect states the full set in its query string anyway.
    set({ connectionId: null, confirmed: [] });
  },

  deliver: (event) => {
    const token = modelToken(event.model);
    const applied = get().appliedSeq[token];
    if (applied !== undefined && event.seq <= applied) {
      // ADR 0023: "drops any live event whose `seq` it has already seen". This
      // is what makes the catch-up read and the live stream safe to overlap —
      // a surface can read `?after=<seq>` and keep receiving at the same time
      // without applying anything twice.
      logger.debug("Duplicate stream event, skipping", {
        model: token,
        seq: event.seq,
      });
      return;
    }
    set((state) => ({ appliedSeq: { ...state.appliedSeq, [token]: event.seq } }));
    for (const listener of get().listeners[token] ?? []) {
      listener.onEvent(event);
    }
  },

  resetModel: (model) => {
    const token = modelToken(model);
    logger.debug("Watch reset by the server", { model: token });
    set((state) => {
      const appliedSeq = { ...state.appliedSeq };
      delete appliedSeq[token];
      // Dropping the token from `confirmed` is what re-watches it: the
      // connection owner syncs `wanted` against `confirmed`, and this makes
      // the model differ again. No timer and no retry counter — the
      // difference IS the instruction.
      return {
        appliedSeq,
        confirmed: state.confirmed.filter((candidate) => candidate !== token),
      };
    });
    for (const listener of get().listeners[token] ?? []) {
      listener.onReset();
    }
  },
}));

/** @internal reset the store between tests. */
export function __resetModelStreamsForTests(): void {
  useModelStreamsStore.setState({
    listeners: {},
    refused: [],
    deferredFromConnect: [],
    wanted: [],
    confirmed: [],
    connectionId: null,
    appliedSeq: {},
  });
}
